import { useMemo, useState } from "react";
import { theme, card, type as text } from "../theme";
import { RegisterToolbar } from "./RegisterToolbar";
import { DimensionHeading } from "./Drawing";
import { PortalLink } from "./PortalLink";
import { KIND_HREFS } from "./EntityTags";
import { by, downloadCsv, matchesSearch, type SortOption } from "../lib/registerExport";
import type { EstateAsset } from "./EstateUnassignedQueue";

// A list of assets with their zone, Purdue level and safety flag, with search, sort and CSV. Used
// for the OT / safety-related view on the estate page and for a zone's own asset list. Rows are a
// summary only: moving an asset between zones is an action on the zone's view.

const SORTS: SortOption<EstateAsset>[] = [
  { key: "name", label: "Name", compare: by.text<EstateAsset>((a) => a.name) },
  { key: "criticality", label: "Criticality", compare: by.rank<EstateAsset>((a) => a.criticality, ["critical", "high", "medium", "low"]) },
  { key: "purdue", label: "Purdue level", compare: by.number<EstateAsset>((a) => a.purdueLevel ?? null, "asc") },
  { key: "zone", label: "Zone", compare: by.text<EstateAsset>((a) => a.zoneId ?? null) },
];

const words = (s: string) => s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

export function EstateAssetTable({ title, assets, zones, purdue, csvName, bare }: {
  title?: string;
  assets: EstateAsset[];
  zones: Array<{ id: string; name: string }>;
  purdue?: Array<{ value: number; label: string }>;
  csvName: string;
  /** Render without the card, inside another panel. */
  bare?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("name");
  const zoneName = useMemo(() => new Map(zones.map((z) => [z.id, z.name])), [zones]);
  const purdueLabel = (v: number | null | undefined) => (v == null ? "" : purdue?.find((l) => l.value === v)?.label ?? String(v / 10));

  const shown = useMemo(() => {
    const rows = assets.filter((a) => matchesSearch(search, a.name, a.kind, a.criticality, a.zoneId ? zoneName.get(a.zoneId) : "no zone"));
    const cmp = SORTS.find((o) => o.key === sort)?.compare;
    return cmp ? [...rows].sort(cmp) : rows;
  }, [assets, search, sort, zoneName]);

  const body = (
    <>
      {title && <DimensionHeading as="h3" count={assets.length}>{title}</DimensionHeading>}
      <RegisterToolbar<EstateAsset>
        search={search}
        onSearch={setSearch}
        placeholder="Search assets"
        sortOptions={SORTS}
        sort={sort}
        onSort={setSort}
        count={{ shown: shown.length, total: assets.length }}
        onExport={() => downloadCsv(csvName, [
          { header: "Asset", value: (a: EstateAsset) => a.name },
          { header: "Kind", value: (a) => words(a.kind) },
          { header: "Criticality", value: (a) => a.criticality },
          { header: "Zone", value: (a) => (a.zoneId ? zoneName.get(a.zoneId) ?? "" : "") },
          { header: "Purdue level", value: (a) => purdueLabel(a.purdueLevel) },
          { header: "Safety related", value: (a) => !!a.safetyRelated },
        ], shown)}
      />
      {shown.length === 0 ? (
        <div style={{ ...text.hint, color: theme.color.faint }}>{assets.length === 0 ? "No assets here." : "No assets match your search."}</div>
      ) : (
        <div style={{ display: "grid", gap: 2 }}>
          {shown.map((a) => (
            <div key={a.id} style={{ ...text.hint, display: "flex", alignItems: "center", gap: 8, padding: "4px 0", borderTop: `1px solid ${theme.color.border}`, flexWrap: "wrap" }}>
              <PortalLink to={KIND_HREFS.asset(a.id) ?? "/resilience/assets"} style={{ color: "var(--c-accentText)", flex: "1 1 160px", minWidth: 0 }}>{a.name}</PortalLink>
              <span style={{ color: theme.color.faint }}>{a.zoneId ? zoneName.get(a.zoneId) ?? "Zone" : "No zone"}</span>
              <span style={{ color: theme.color.faint }}>{purdueLabel(a.purdueLevel) || "No Purdue level"}</span>
              <span style={{ color: theme.color.subtle }}>{a.criticality}</span>
              {a.safetyRelated && <span style={{ fontSize: 10, fontWeight: 700, color: theme.color.dangerText }}>Safety</span>}
            </div>
          ))}
        </div>
      )}
    </>
  );
  return bare ? body : <div style={{ ...card, marginBottom: 16 }}>{body}</div>;
}
